import 'ag-grid-enterprise';
import type { IServerSideDatasource, IServerSideGetRowsParams } from 'ag-grid-community';
import { client } from '@/api/backoffice-api';
import AppConfig from '@/utils/app-config.mjs';
import logger from '@/utils/logger.mjs';

export type EcommerceStatusActionCode = 'archive' | 'archive_and_notify_dealer' | 'unarchive';

export interface EcommerceStatusMappingRow {
  /** Server mapping id when loaded from API (numeric from JSON is preserved) */
  id?: string | number;
  ecommerceStatus: string;
  /** API action value (archive | archive_and_notify_dealer | unarchive) */
  action: EcommerceStatusActionCode | string;
  description: string;
}

const CACHE_BLOCK_SIZE = 100;

const LEGACY_ACTION_LABEL_TO_CODE: Record<string, EcommerceStatusActionCode> = {
  'cancel / archive': 'archive',
  'halt / archive': 'archive_and_notify_dealer',
  'archive transfer': 'archive',
  'archive and notify dealer': 'archive_and_notify_dealer',
  unarchive: 'unarchive'
};

function coerceActionCode(raw: string): EcommerceStatusActionCode | string {
  const t = raw.trim();
  if (t === 'archive' || t === 'archive_and_notify_dealer' || t === 'unarchive') {
    return t;
  }
  const mapped = LEGACY_ACTION_LABEL_TO_CODE[t.toLowerCase()];
  return mapped ?? t;
}

function normalizeRecord(item: Record<string, unknown>): EcommerceStatusMappingRow {
  const idRaw =
    item.id ??
    item.mappingId ??
    item.mapping_id ??
    item.ecommerce_status_mapping_id ??
    item.ecommerceStatusMappingId;
  let id: string | number | undefined;
  if (idRaw != null && String(idRaw).trim() !== '') {
    id =
      typeof idRaw === 'number' && Number.isFinite(idRaw)
        ? idRaw
        : String(idRaw).trim();
  }
  const actionRaw = String(
    item.transferAction ?? item.transfer_action ?? item.action ?? ''
  ).trim();
  return {
    id,
    ecommerceStatus: String(
      item.ecommerceStatus ?? item.ecommerce_status ?? item.status ?? ''
    ).trim(),
    action: coerceActionCode(actionRaw),
    description: String(item.description ?? item.desc ?? '').trim()
  };
}

function parseListResponse(body: unknown): EcommerceStatusMappingRow[] {
  const list: unknown[] = Array.isArray(body)
    ? body
    : body != null && typeof body === 'object' && Array.isArray((body as { data?: unknown[] }).data)
      ? (body as { data: unknown[] }).data
      : body != null && typeof body === 'object' && Array.isArray((body as { items?: unknown[] }).items)
        ? (body as { items: unknown[] }).items
        : body != null && typeof body === 'object' && Array.isArray((body as { mappings?: unknown[] }).mappings)
          ? (body as { mappings: unknown[] }).mappings
          : [];
  return list.map((raw) =>
    normalizeRecord(
      typeof raw === 'object' && raw != null ? (raw as Record<string, unknown>) : {}
    )
  );
}

type LoadStateListener = (state: { loading: boolean }) => void;

/**
 * Server-side datasource for e-commerce status → transfer action mappings (FFL Express).
 * Same pattern as LicenseReviewDataSource (review/ducks/licenseReviewDatasource.ts).
 */
export default class EcommerceStatusActionMappingDataSource implements IServerSideDatasource {
  private storeKey: string | undefined;

  private rows: EcommerceStatusMappingRow[] | null = null;

  private inflight: Promise<EcommerceStatusMappingRow[]> | null = null;

  /** Incremented on every {@link setStoreId} so in-flight responses are ignored when stale */
  private fetchGeneration = 0;

  private onLoadState?: LoadStateListener;

  setLoadStateListener(cb: LoadStateListener | undefined): void {
    this.onLoadState = cb;
  }

  /**
   * When store context changes, clear cache so the next getRows refetches.
   * Pass undefined / empty for global-admin (no storeId query param).
   */
  setStoreId(storeId: string | undefined): void {
    const next = storeId?.trim() || undefined;
    this.storeKey = next;
    this.rows = null;
    this.inflight = null;
    this.fetchGeneration += 1;
  }

  getAllRows(): EcommerceStatusMappingRow[] {
    return this.rows ?? [];
  }

  /** Used after local add / edit / delete until persistence APIs exist */
  setAllRows(next: EcommerceStatusMappingRow[]): void {
    this.rows = next;
  }

  /** Drop cached list so the next grid fetch hits the API again */
  invalidateCache(): void {
    this.rows = null;
    this.inflight = null;
  }

  private async fetchFromApi(): Promise<EcommerceStatusMappingRow[]> {
    const response = await client.get(
      `${AppConfig.fflExpressServiceApiURL}/dealers/ecommerce-status-action-mappings`,
      { timeout: 30000 }
    );
    return parseListResponse(response.data);
  }

  private ensureRows(): Promise<EcommerceStatusMappingRow[]> {
    if (this.rows !== null) {
      return Promise.resolve(this.rows);
    }
    if (this.inflight) {
      return this.inflight;
    }
    this.onLoadState?.({ loading: true });
    const genAtFetch = this.fetchGeneration;
    const p = this.fetchFromApi()
      .then((data) => {
        if (genAtFetch !== this.fetchGeneration) {
          return [] as EcommerceStatusMappingRow[];
        }
        this.rows = data;
        this.onLoadState?.({ loading: false });
        return data;
      }) // stale responses must not clear loading; the active fetch owns onLoadState
      .catch((err: unknown) => {
        logger.error('EcommerceStatusActionMappingDataSource fetch failed', err);
        if (genAtFetch !== this.fetchGeneration) {
          return [] as EcommerceStatusMappingRow[];
        }
        this.rows = [];
        // Per UX requirement, don't surface raw API errors in UI for this grid.
        this.onLoadState?.({ loading: false });
        return this.rows;
      })
      .finally(() => {
        if (this.inflight === p) {
          this.inflight = null;
        }
      });
    this.inflight = p;
    return p;
  }

  getRows(params: IServerSideGetRowsParams): void {
    this.ensureRows()
      .then((all) => {
        const startRow = params.request.startRow ?? 0;
        const endRow = params.request.endRow ?? all.length;
        const pageRows = all.slice(startRow, endRow);
        params.success({
          rowData: pageRows,
          rowCount: all.length
        });
        if (all.length === 0) {
          params.api.showNoRowsOverlay();
        } else {
          params.api.hideOverlay();
        }
      })
      .catch((err: unknown) => {
        logger.error('EcommerceStatusActionMappingDataSource getRows failed', err);
        params.success({
          rowData: [],
          rowCount: 0
        });
        params.api.showNoRowsOverlay();
      });
  }
}

export const ECOMMERCE_STATUS_MAPPING_CACHE_BLOCK_SIZE = CACHE_BLOCK_SIZE;
